import { useState, type FormEvent } from 'react'
import { Check } from 'lucide-react'
import PageContainer from '@/components/layout/PageContainer'
import Card from '@/components/layout/Card'
import PasswordStrengthMeter from '@/components/PasswordStrengthMeter'
import EmailIdentityEditor from '@/features/team/EmailIdentityEditor'
import { useChangeMyPassword, useMe, useUpdateMe, useUploadMyPhoto } from '@/features/team/useTeam'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'
import type { User } from '@/api/users'

const inputCls =
  'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40'

const ROLE_LABEL: Record<string, string> = { AGENCY_ADMIN: 'Admin', AGENT: 'Agent', PLATFORM_ADMIN: 'Platform admin' }

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
    </div>
  )
}

/**
 * My profile: what any user — not just an admin — can change about themselves: their name and phone,
 * the photo and signature their emails go out with, and their password. Opened from the user card at
 * the bottom of the sidebar.
 */
export default function MyProfile() {
  const { data: me, isLoading } = useMe()
  return (
    <PageContainer width="wide">
      <div className="mb-5">
        <h1 className="text-2xl font-semibold text-brand-darkest">My profile</h1>
        <p className="text-sm text-gray-500">Your details, the photo and signature your emails go out with, and your password.</p>
      </div>
      {isLoading || !me ? <p className="text-sm text-gray-500">Loading…</p> : <ProfileForms key={me.id} me={me} />}
    </PageContainer>
  )
}

function ProfileForms({ me }: { me: User }) {
  const update = useUpdateMe()
  const upload = useUploadMyPhoto()
  const [fullName, setFullName] = useState(me.fullName)
  const [phone, setPhone] = useState(me.phone ?? '')
  const [photoUrl, setPhotoUrl] = useState(me.photoUrl ?? '')
  const [signature, setSignature] = useState(me.emailSignature ?? '')
  const [photoError, setPhotoError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const dirty =
    fullName !== me.fullName ||
    phone !== (me.phone ?? '') ||
    photoUrl !== (me.photoUrl ?? '') ||
    signature !== (me.emailSignature ?? '')

  function save() {
    if (!fullName.trim()) return
    setSaved(false)
    update.mutate(
      // empty string clears the value on the backend
      { fullName: fullName.trim(), phone: phone.trim(), emailSignature: signature.trim(), photoUrl: photoUrl.trim() },
      { onSuccess: () => setSaved(true) },
    )
  }

  return (
    <div className="grid gap-5 lg:grid-cols-2 items-start">
      <div className="space-y-5">
        <Card title="Your details">
          <div className="space-y-3">
            <Field label="Name">
              <input className={inputCls} value={fullName} onChange={(e) => { setFullName(e.target.value); setSaved(false) }} />
            </Field>
            <Field label="Phone" hint="Shown to colleagues on deals you own, and available to your emails as “My phone”.">
              <input className={inputCls} inputMode="tel" value={phone} placeholder="e.g. 0412 345 678" onChange={(e) => { setPhone(e.target.value); setSaved(false) }} />
            </Field>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Field label="Sign-in email" hint="Ask an admin to change it.">
                <div className="py-2 text-gray-700 truncate">{me.email}</div>
              </Field>
              <Field label="Role">
                <div className="py-2 text-gray-700">{ROLE_LABEL[me.role] ?? me.role}</div>
              </Field>
            </div>
          </div>
        </Card>
        <PasswordCard />
      </div>

      <Card title="Photo & email signature">
        <EmailIdentityEditor
          photoUrl={photoUrl}
          onPhotoUrlChange={(u) => { setPhotoUrl(u); setSaved(false) }}
          signature={signature}
          onSignatureChange={(s) => { setSignature(s); setSaved(false) }}
          uploading={upload.isPending}
          photoError={photoError}
          onUpload={(file) => {
            setPhotoError(null)
            upload.mutate(file, { onSuccess: (u) => setPhotoUrl(u.photoUrl ?? ''), onError: (err) => setPhotoError(apiErrorMessage(err)) })
          }}
        />
        <div className="flex items-center justify-end gap-3 mt-4 pt-3 border-t border-gray-100">
          {update.isError && <p className="text-danger text-xs mr-auto">{apiErrorMessage(update.error)}</p>}
          {saved && !dirty && (
            <span className="inline-flex items-center gap-1 text-xs text-success"><Check size={13} /> Saved</span>
          )}
          <button
            type="button"
            onClick={save}
            disabled={!dirty || !fullName.trim() || update.isPending}
            className="px-4 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark disabled:opacity-50"
          >
            {update.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </Card>
    </div>
  )
}

function PasswordCard() {
  const change = useChangeMyPassword()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [done, setDone] = useState(false)
  const mismatch = confirm !== '' && confirm !== next

  function submit(e: FormEvent) {
    e.preventDefault()
    if (!current || !next || mismatch) return
    setDone(false)
    change.mutate(
      { current, next },
      {
        onSuccess: () => {
          setDone(true)
          setCurrent('')
          setNext('')
          setConfirm('')
        },
      },
    )
  }

  return (
    <Card title="Password">
      <form onSubmit={submit} className="space-y-3">
        <Field label="Current password">
          <input type="password" autoComplete="current-password" className={inputCls} value={current} onChange={(e) => setCurrent(e.target.value)} />
        </Field>
        <Field label="New password">
          <input type="password" autoComplete="new-password" className={inputCls} value={next} onChange={(e) => setNext(e.target.value)} />
        </Field>
        <div className="pt-2"><PasswordStrengthMeter password={next} /></div>
        <Field label="Type the new password again">
          <input type="password" autoComplete="new-password" className={inputCls} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </Field>
        {mismatch && <p className="text-danger text-xs">The two new passwords don’t match.</p>}
        {change.isError && <p className="text-danger text-xs">{apiErrorMessage(change.error)}</p>}
        {done && <p className="inline-flex items-center gap-1 text-xs text-success"><Check size={13} /> Password changed — use it next time you sign in.</p>}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={!current || !next || !confirm || mismatch || change.isPending}
            className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-brand-dark hover:bg-gray-50 disabled:opacity-50"
          >
            {change.isPending ? 'Changing…' : 'Change password'}
          </button>
        </div>
      </form>
    </Card>
  )
}
